import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Home, Users, Star, Check, ArrowRight, ChevronLeft, ChevronRight, X,
  Wifi, Wind, Droplets, Tv, Car, Coffee, Flame,
} from "lucide-react";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { useSettings } from "../context/SettingsContext";
import { calcRoomTotal } from "../utils/pricing";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE  = process.env.REACT_APP_BACKEND_URL || "";

const AMENITY_ICONS = { Wifi, AC: Wind, "Hot Water": Droplets, TV: Tv, Parking: Car, Kitchen: Coffee, Geyser: Flame };

function imgSrc(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BE}${url}`;
}

export default function RoomDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hotelName, address } = useSettings();
  const [room, setRoom]       = useState(null);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [checkIn, setCheckIn]   = useState("");
  const [checkOut, setCheckOut] = useState("");

  useEffect(() => {
    Promise.all([
      fetch(`${API}/rooms/public/${id}`).then(r => r.json()),
      fetch(`${API}/reviews/public?roomId=${id}`).then(r => r.json()),
    ]).then(([roomJson, reviewJson]) => {
      setRoom(roomJson.data || null);
      setReviews(reviewJson.data || []);
      setSummary(reviewJson.summary || { average: 0, count: 0 });
    }).finally(() => setLoading(false));
  }, [id]);

  const nights = checkIn && checkOut
    ? Math.max(0, Math.ceil((new Date(checkOut) - new Date(checkIn)) / 86400000))
    : 0;

  function bookNow() {
    const qs = new URLSearchParams({ roomId: id, ...(checkIn ? { checkIn } : {}), ...(checkOut ? { checkOut } : {}) });
    navigate(`/book?${qs}`);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Room Not Found</h2>
          <a href="/" className="text-amber-600 font-semibold hover:underline">← Back to {hotelName}</a>
        </div>
      </div>
    );
  }

  const photos = room.photos?.length ? room.photos : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-orange-50/20">
      <header className="sticky top-0 z-20 bg-white/60 backdrop-blur-xl border-b border-white/40 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="tap-44 flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500
              flex items-center justify-center shadow-md">
              <Home size={17} className="text-white" />
            </div>
            <p className="font-black text-gray-900 text-sm">{hotelName}</p>
          </a>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Photo gallery */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-4 grid-rows-2 gap-2 rounded-3xl overflow-hidden mb-6 h-[380px]">
            <button onClick={() => setLightbox(0)} className="col-span-2 row-span-2 relative group">
              <img src={imgSrc(photos[0])} alt={room.name} loading="eager" fetchpriority="high" className="w-full h-full object-cover group-hover:brightness-90 transition-all" />
            </button>
            {photos.slice(1, 5).map((p, i) => (
              <button key={i} onClick={() => setLightbox(i + 1)} className="relative group">
                <img src={imgSrc(p)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:brightness-90 transition-all" />
                {i === 3 && photos.length > 5 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold text-sm">
                    +{photos.length - 5} more
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="h-64 rounded-3xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-6">
            <Home size={40} className="text-amber-300" />
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          {/* Left: details */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">{room.type}</span>
                  <h1 className="text-3xl font-black text-gray-900 mt-1">{room.name}</h1>
                </div>
                {summary.count > 0 && (
                  <div className="flex items-center gap-1.5 bg-amber-50/60 backdrop-blur-md border border-amber-200/60 rounded-xl px-3 py-2 flex-shrink-0">
                    <Star size={14} className="text-amber-500 fill-amber-500" />
                    <span className="font-black text-amber-800 text-sm">{summary.average}</span>
                    <span className="text-xs text-amber-600">({summary.count})</span>
                  </div>
                )}
              </div>
              <p className="flex items-center gap-1.5 text-sm text-gray-500 mt-2">
                <Users size={14} /> {room.baseOccupancy || room.capacity} guests · {address}
              </p>
            </div>

            {room.description && (
              <p className="text-gray-600 leading-relaxed">{room.description}</p>
            )}

            {room.amenities?.length > 0 && (
              <div>
                <h3 className="font-bold text-gray-900 mb-3">What this place offers</h3>
                <div className="grid grid-cols-2 gap-3">
                  {room.amenities.map(a => {
                    const Icon = AMENITY_ICONS[a] || Check;
                    return (
                      <div key={a} className="flex items-center gap-2 text-sm text-gray-600">
                        <Icon size={16} className="text-amber-500" /> {a}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Availability calendar */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Availability</h3>
              <AvailabilityCalendar roomId={id} checkIn={checkIn} checkOut={checkOut}
                onChange={(ci, co) => { setCheckIn(ci); setCheckOut(co); }} />
            </div>

            {/* Reviews */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Star size={16} className="text-amber-500 fill-amber-500" /> Guest Reviews
                {summary.count > 0 && <span className="text-gray-400 font-normal text-sm">({summary.count})</span>}
              </h3>
              {reviews.length === 0 ? (
                <p className="text-sm text-gray-400">No reviews yet for this room.</p>
              ) : (
                <div className="space-y-4">
                  {reviews.slice(0, 6).map(r => (
                    <div key={r._id} className="bg-white/50 backdrop-blur-lg rounded-2xl border border-white/50 p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="font-bold text-gray-900 text-sm">{r.guestName}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} size={12} className={i < r.rating ? "text-amber-500 fill-amber-500" : "text-gray-200"} />
                          ))}
                        </div>
                      </div>
                      {r.comment && <p className="text-sm text-gray-600 leading-relaxed">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: sticky booking widget */}
          <div className="md:col-span-1">
            <div className="sticky top-24 bg-white/55 backdrop-blur-2xl rounded-3xl shadow-2xl shadow-amber-900/5 border border-white/50 p-6">
              {/* Seasonal overrides were ignored here entirely: this read
                  room.pricePerNight and multiplied it by nights, so a night
                  carrying an override quoted the OLD rate and, worse, a WRONG
                  TOTAL — a guest could be shown ₹2,000 here and charged
                  ₹5,000 at checkout. Now uses calcRoomTotal, the same function
                  the booking page and the backend price with, so all three
                  agree. */}
              <p className="text-2xl font-black text-gray-900">
                ₹{(nights > 0
                    ? Math.round(calcRoomTotal(room, room.baseOccupancy || room.capacity || 1, checkIn, checkOut) / nights)
                    : (room.pricePerNight || 0)
                  ).toLocaleString("en-IN")}
                <span className="text-sm font-normal text-gray-400"> / night</span>
              </p>
              {nights > 0 && (() => {
                const stayTotal = calcRoomTotal(room, room.baseOccupancy || room.capacity || 1, checkIn, checkOut);
                return (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>{nights} night{nights > 1 ? "s" : ""}</span>
                      <span className="font-bold text-gray-900">₹{stayTotal.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                );
              })()}
              <button onClick={bookNow}
                className="w-full mt-4 py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500
                  text-white font-bold text-sm hover:from-amber-600 hover:to-orange-600 shadow-md
                  transition-all flex items-center justify-center gap-2">
                {checkIn && checkOut ? "Book Now" : "Select Dates to Book"} <ArrowRight size={15} />
              </button>
              <p className="text-xs text-gray-400 text-center mt-3">You won't be charged yet — we'll confirm by WhatsApp</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && photos[lightbox] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white"><X size={28} /></button>
          {lightbox > 0 && (
            <button onClick={e => { e.stopPropagation(); setLightbox(l => l - 1); }}
              className="absolute left-6 text-white p-2"><ChevronLeft size={32} /></button>
          )}
          <img src={imgSrc(photos[lightbox])} alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-xl" onClick={e => e.stopPropagation()} />
          {lightbox < photos.length - 1 && (
            <button onClick={e => { e.stopPropagation(); setLightbox(l => l + 1); }}
              className="absolute right-6 text-white p-2"><ChevronRight size={32} /></button>
          )}
        </div>
      )}
    </div>
  );
}
